import { Item } from '@react-stately/collections';
import { Container } from '@geberit/gdds';

// styles
import styles from './sliding-tabs.module.scss';

// types
import type { SlidingTabsProps } from './sliding-tabs.types';
import { Formats } from '../headline/headlines.types';

// components
import Tabs from './tabs';
import Content from './tab-content';
import { Headline } from '../headline/headline';
import { InnerHtml } from 'components/InnerHtml/inner-html';

// utils
import { buildSize, gridSizes } from 'utils/gridSize';
import { useSectionId } from 'utils/hooks/use-section-id';
import { useXy } from 'utils/hooks/use-xy';

const generateKey = (tab: Tab) => {
  const uniqItemData = tab.items.map((item) => item.previewId).join('-');
  return `${tab.label}-${uniqItemData}`;
};

export function SlidingTabs({
  tabs,
  title,
  text,
  subtitle,
  anchor,
  titleInAnchor,
  contentIndex,
  disabledKeys = [],
}: Readonly<SlidingTabsProps>) {
  const sectionId = useSectionId(title, anchor, titleInAnchor, contentIndex);
  const isXY = useXy();

  return (
    <section id={sectionId}>
      <Container maxContentWidth={buildSize(gridSizes.gddsFullGrid)} className={styles.tabsWrapper}>
        <Headline
          className={styles.headlineWrapper}
          tag={Formats.h2}
          title={title}
          subtitle={subtitle}
          text={text}
        />
        <div className={styles.container}>
          <div data-preview-id="#st_tabs">
            {tabs.length > 0 && (
              <Tabs aria-label={title} disabledKeys={disabledKeys}>
                {tabs.map((tab, currentIndex) => (
                  <Item
                    key={generateKey(tab)}
                    title={
                      <InnerHtml
                        className={styles.tabTitle}
                        as={isXY ? 'h3' : 'span'}
                        content={tab.label}
                        isGdds
                        previewId="#st_tabLabel"
                      />
                    }
                  >
                    <Content content={tab.items} currentIndex={currentIndex} />
                  </Item>
                ))}
              </Tabs>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}
